import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../../hooks/use-toast';
import { sysConfigApiService, type SysConfigEntry } from '../../../../../features/scopingAi/services/sysConfigApiService';
import {
  REPORT_ATTACHMENT_CONFIG,
  reportAttachmentFormat,
  type ReportAttachmentFormat,
} from '../../../../../features/billValidation/lib/reportAttachment';
import { FileText, Save, RefreshCw, AlertCircle } from 'lucide-react';

// ── Bill validation report attachment — PDF or HTML, one per email ───────────
//
// Super Admin → Report Attachment. sys_config.bv_report_attachment.value shape:
// { format: 'pdf' | 'html' }. No row means PDF. Read by the send dialogs and the
// send route (reportAttachment.ts).

const ATTACHMENT_OPTIONS: { value: ReportAttachmentFormat; label: string; hint: string }[] = [
  { value: 'pdf', label: 'PDF', hint: 'The report as a PDF document — what clients receive today.' },
  { value: 'html', label: 'HTML', hint: 'The report as a web page the client opens in a browser.' },
];

export const ReportAttachmentSettings: React.FC = () => {
  const { toast } = useToast();
  const [entry, setEntry] = useState<SysConfigEntry | null>(null);
  const [format, setFormat] = useState<ReportAttachmentFormat>('pdf');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // The list, not get(): a missing row is the normal "never set" state and
      // get() reports it as a 404.
      const result = await sysConfigApiService.list();
      if (!mounted) return;
      if (result.success) {
        const row = (result.data ?? []).find(e => e.name === REPORT_ATTACHMENT_CONFIG) ?? null;
        setEntry(row);
        setFormat(reportAttachmentFormat(row?.value));
      } else {
        setLoadError(result.error || 'Failed to load the report attachment setting');
      }
      setIsLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const saved = reportAttachmentFormat(entry?.value);

  const handleSave = async () => {
    setIsSaving(true);
    const result = await sysConfigApiService.upsert(
      REPORT_ATTACHMENT_CONFIG,
      { format },
      entry?.description ?? 'Which file bill validation report emails carry: "pdf" or "html". One only — both together exceed the upload limit.'
    );
    setIsSaving(false);
    if (result.success && result.data) {
      setEntry(result.data);
      toast({ title: 'Saved', description: `Report emails will carry the ${format.toUpperCase()}.` });
    } else {
      toast({ title: 'Save failed', description: result.error, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Report Attachment
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          The file attached to bill validation report emails.
        </p>
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : !loadError && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Bill Validation Report Attachment</CardTitle>
            <CardDescription>
              Applies to single sends and bulk sends alike. One or the other, not both: the two
              together are too large to upload.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div role="radiogroup" aria-label="Report attachment" className="grid gap-2 sm:grid-cols-2">
              {ATTACHMENT_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={[
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer',
                    format === option.value ? 'border-primary bg-primary/5' : 'bg-gray-50/60 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="bv-report-attachment"
                    value={option.value}
                    checked={format === option.value}
                    onChange={() => setFormat(option.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.hint}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={isSaving || format === saved} size="sm" className="gap-1.5">
                {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {isSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
